import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import { QuoteFormProvider } from "./components/QuoteFormContext";
import PremiumLoader from "./components/PremiumLoader";
import VisitPopup from "./components/VisitPopup";
import ErrorBoundary from "./components/ErrorBoundary";
import { armThirdPartyGate } from "@/lib/third-party";

// The homepage is eager (it is the primary landing + LCP target); all other
// routes are code-split so their JS (incl. Leaflet) stays out of the initial load.
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background" aria-hidden="true">
    <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary motion-safe:animate-spin" />
  </div>
);

const App = () => {
  const [loaderDone, setLoaderDone] = useState(false);

  useEffect(() => {
    armThirdPartyGate();
  }, []);

  return (
    <QuoteFormProvider>
      <PremiumLoader onDone={() => setLoaderDone(true)} />
      <VisitPopup ready={loaderDone} />
      <BrowserRouter>
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/about" element={<About />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/locations/:slug" element={<LocationDetail />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </QuoteFormProvider>
  );
};

export default App;
